import { gql } from '@apollo/client';

/* ── model portfolios / mandates ────────────────────────────── */

/**
 * The model register, with the one field that says where a model came from.
 *
 * `getAssetModelCompleteData` below answers a richer row — allocations, bands,
 * sub-splits — but on a type that carries no `source`, so it cannot say whose
 * model this is. This one can: everything published by the platform reads
 * "KeepInvesting", so anything else was written here.
 */
/**
 * The asset classes a model can allocate to, and the SEBI categories inside
 * each one.
 *
 * Two reads rather than one: the class list is the complete register, while
 * the categories come back only on the classes that have them — so the names
 * are taken from the first and the categories merged in from the second by id.
 */
/**
 * The model register, as one row per model with everything on it.
 *
 * This is the read the list is built from, and the reason is population:
 * `getAssetModelCompleteData` answers ~200 models on a type carrying no
 * `category`, `source` or `isActive`, while this answers the 36 published
 * models with all of them. Joining the two by id matched only 23 — which drew
 * a table of blanks. One source, every column, rather than two and a gap.
 */
export const GET_COMPLETE_ASSET_MODELS = gql`
  query GetCompleteAssetModels($limit: Int = 200, $filters: AssetModelFilterInput) {
    getCompleteAssetModels(limit: $limit, filters: $filters) {
      id
      name
      modelStandardName
      source
      description
      category
      currency
      riskPreference
      timeHorizon
      isActive
      isListable
      allocations {
        assetClassId
        weight
        toleranceBand
        assetClass { id name }
      }
    }
  }
`;

/**
 * Every model with the funds its variants actually hold.
 *
 * This is the only way to answer "which models use this scheme": the backend
 * has no reverse lookup — getPortfoliosBySymbols and its siblings fetch an
 * entity by its OWN symbol, and nothing maps a holding back to the models
 * carrying it. Rather than 36 per-model reads, includeVariants brings every
 * model's variants and their funds in one call, and the match is made here.
 *
 * `weightWithinAssetModel` is a FRACTION as a string ("0.2084"), like every
 * other model weight in this file — not a percentage.
 */
export const GET_ASSET_MODELS_WITH_FUNDS = gql`
  query GetAssetModelsWithFunds($limit: Int = 200) {
    getCompleteAssetModels(limit: $limit, includeVariants: true) {
      id
      name
      category
      source
      riskPreference
      isActive
      isListable
      variants {
        variantId
        variantName
        securitySymbol
        funds {
          holdingSecuritySymbol
          securityName
          weightWithinAssetModel
        }
      }
    }
  }
`;

/**
 * Per-variant measurement: risk, realized figures and exposures.
 *
 * A separate read from the model record because it answers a different
 * question — not what the model targets, but how the thing holding it has
 * actually behaved. Keyed on the model and returning its variants.
 *
 * UNITS, which the resolver does not advertise: the measured figures
 * (`volatility`, `maxDrawdown`, `trackingError1y`, the returns) are already
 * PERCENTAGES, while every `realized*` field is a FRACTION. A model whose
 * volatility reads 0.3685 has realizedVolatility 0.0037 — the same number, a
 * hundred apart. Multiplying both by 100 is how 0.37% becomes 37%.
 *
 * The exposure fields are untyped JSON: `sectorExposures` and
 * `assetClassExposures` are lists of `{ name, weight, as_of }` with weight as
 * a fraction; `factorExposures` here is a snake_case object, unlike the typed
 * camelCase one on the model record.
 */
export const GET_ASSET_MODEL_VARIANT_METRICS = gql`
  query GetAssetModelVariantMetrics($modelId: Int!, $topN: Int! = 5) {
    getAssetModelImplementationVariants(modelId: $modelId, topN: $topN) {
      variantId
      variantName
      variantDescription
      version
      isLatest
      securitySymbol
      minInvestmentAmount
      sipMinInvestmentAmount
      measuredAsOf
      realizedAsOf
      expenseRatio

      return1y
      return3y
      return5y
      annualizedReturn1y
      annualizedReturn3y

      volatility
      volatility3y
      downsideDeviation1y
      var951m
      var991m
      cvar951m
      maxDrawdown
      currentDrawdown
      drawdownDurationDays
      sharpeRatio
      sortinoRatio
      calmarRatio
      beta
      alpha
      correlationToBenchmark1y
      trackingError1y
      informationRatio1y

      realizedVolatility
      realizedVar95
      realizedCvar95
      realizedSharpeRatio
      realizedSortinoRatio
      realizedMaxDrawdown
      realizedAlpha
      realizedBetaToBenchmark
      realizedTrackingError

      benchmarkAnnualizedReturn1y
      benchmarkAnnualizedReturn3y
      trackingErrorBudget
      withinBudget

      sectorExposures
      assetClassExposures
    }
  }
`;

/**
 * One model, in full — including how it is actually implemented.
 *
 * A model is a target allocation; a VARIANT is a way of holding it, and a
 * model publishes several (cheapest, best, lowest SIP). Each names its own
 * security symbol, its rebalancing frequency and minimums, the funds that
 * implement it, and the look-through holdings underneath those funds — which
 * run to a couple of hundred names.
 *
 * `metrics` is deliberately not selected: it is a hundred-field performance
 * bag, and nothing here reads it yet.
 *
 * Each fund carries `assetClassId` and `sebiCategoryId` — the SLOT it sits in,
 * not decoration. `upsertAssetModelFunds` keys every update and removal on
 * (assetClassId, sebiCategoryId, securitySymbol) and raises when that triple
 * matches no row the variant holds, so the constituents editor cannot write a
 * change to a fund whose slot it did not read back. `weightWithinSebiCategory`
 * is the fund's share OF ITS CATEGORY, normalised against its siblings — the
 * only weight that mutation accepts, with the model weight following from the
 * category's own share of the model.
 */
export const GET_ASSET_MODEL_DETAIL = gql`
  query GetAssetModelDetail($id: Int!) {
    getAssetModelById(id: $id) {
      id
      name
      modelStandardName
      source
      description
      category
      currency
      riskPreference
      timeHorizon
      goldPreference
      isActive
      isListable
      allocations {
        id
        assetClassId
        weight
        toleranceBand
        rebalancingFrequency
        minCapital
        assetClass { id name classType description parentId }
        sebiCategoryAllocations {
          id
          sebiCategoryId
          sebiCategoryAllocation
          portfolioWeight
          sebiCategory { id name }
        }
      }
      variants {
        variantId
        variantKey
        variantName
        variantDescription
        securitySymbol
        rebalancingFrequency
        minInvestmentAmount
        sipMinInvestmentAmount
        funds {
          holdingSecuritySymbol
          securityName
          assetClassName
          weightWithinAssetModel
          weightWithinAssetClass
          weightWithinSebiCategory
          isActive
          assetClassId
          sebiCategoryId
        }
        holdings {
          securitySymbol
          name
          weight
          sector
          assetClass
          securityType
          asOfDate
        }
        # Trailing returns and headline risk, already as percentages.
        metrics {
          return1m return3m return6m returnYtd return1y return3y return5y
          sharpeRatio sortinoRatio volatility beta alpha expenseRatio
        }
        # The TYPED factor scores — camelCase here, unlike the JSON blob the
        # variant-metrics read returns.
        factorExposures {
          asOfDate
          beta
          rSquared
          alphaScore
          momentumScore
          shortTermMomentumScore
          longTermMomentumScore
          valueScore
          qualityScore
          sizeScore
          growthScore
          highVolatilityScore
          lowVolatilityScore
        }
      }
    }
  }
`;

export const GET_ASSET_CLASSES_BASIC = gql`
  query GetAssetClassesBasic($language: String) {
    getAssetClasses(language: $language) {
      id
      name
      classType
      description
    }
  }
`;

export const GET_ASSET_CLASSES_WITH_SEBI = gql`
  query GetAssetClassesWithSebiCategories {
    getAssetClassesWithSebiCategories {
      id
      name
      code
      sebiCategories { id name code }
    }
  }
`;

/**
 * Every version of every implementation (variant) of a model, with the funds
 * each version held. One row per (variant, version); `isLatest` marks the
 * version in force. `isLatest: false` asks for the whole history —
 * `getAssetModelById`'s own variants are not guaranteed to be the latest.
 */
export const GET_MODEL_VARIANT_VERSIONS = gql`
  query GetModelVariantVersions($modelId: Int!, $topN: Int!) {
    getAssetModelImplementationVariants(
      modelId: $modelId
      topN: $topN
      isLatest: false
      includePriceSeries: false
    ) {
      variantId
      variantKey
      variantName
      version
      isLatest
      securitySymbol
      measuredAsOf
      realizedAsOf
      implementations {
        id
        assetClassId
        sebiCategoryId
        holdingSecuritySymbol
        securityName
        weightWithinSebiCategory
        weightWithinAssetClass
        weightWithinAssetModel
        isActive
      }
    }
  }
`;

/**
 * The full measurement of every (variant, version) of a model — what the
 * constituents editor's Performance panel reads.
 *
 * `version: 0` answers EVERY version; the "latest only" view is filtered on
 * the client. `includePriceSeries` brings each row's daily `return1d` (a
 * percentage), which the growth chart compounds.
 *
 * UNITS, as on GET_ASSET_MODEL_VARIANT_METRICS: measured figures and returns
 * are PERCENTAGES, every `realized*` is a FRACTION. In the holding rows,
 * `volatility` and the `maxDrawdown*` are already weight-scaled (they sum to
 * the variant's own), while `trackingError` is the fund's OWN tracking error
 * against the model's benchmark — Σ weight × TE is the undiversified total.
 * Their `weight` comes back null, so it is joined from `implementations`.
 */
export const GET_MODEL_VARIANT_PERFORMANCE = gql`
  query GetModelVariantPerformance($modelId: Int!, $topN: Int!, $version: Int, $includePriceSeries: Boolean!) {
    getAssetModelImplementationVariants(
      modelId: $modelId
      topN: $topN
      version: $version
      includePriceSeries: $includePriceSeries
    ) {
      variantId variantKey variantName version isLatest securitySymbol
      measuredAsOf realizedAsOf expenseRatio minInvestmentAmount sipMinInvestmentAmount

      return1d return1w
      cumulativeReturn1m cumulativeReturn3m cumulativeReturn6m cumulativeReturnYtd cumulativeReturn1y
      return1y return3y return5y return10y returnSinceInception
      annualizedReturn1y annualizedReturn3y annualizedReturn5y annualizedReturn10y annualizedReturnSinceInception
      benchmarkCumulativeReturn1m benchmarkCumulativeReturn2m benchmarkCumulativeReturn3m benchmarkCumulativeReturn1y
      benchmarkCumulativeReturn3y benchmarkCumulativeReturn5y
      benchmarkAnnualizedReturn1y benchmarkAnnualizedReturn3y benchmarkAnnualizedReturn5y

      volatility volatility3m volatility6m volatility3y volatility5y
      downsideDeviation1m downsideDeviation3m downsideDeviation1y
      var951d var951w var951m var991d var991w var991m
      cvar951d cvar951w cvar951m cvar991d cvar991w cvar991m
      maxDrawdown currentDrawdown drawdownDurationDays
      sharpeRatio sharpeRatio3y sharpeRatio5y sortinoRatio sortinoRatio3y calmarRatio
      beta beta3y alpha alpha3y
      correlationToBenchmark1y correlationToBenchmark3y
      trackingError1y trackingError3y informationRatio1y informationRatio3y
      trackingErrorBudget withinBudget

      realizedVolatility realizedVar95 realizedVar99 realizedCvar95 realizedCvar99
      realizedExpectedReturn realizedSharpeRatio realizedSortinoRatio
      realizedMaxDrawdown realizedMaxDrawdown6m realizedMaxDrawdown1y realizedMaxDrawdown3y realizedMaxDrawdown5y
      realizedAlpha realizedBetaToBenchmark realizedTrackingError

      factorExposures sectorExposures industryExposures countryExposures assetClassExposures
      benchmarkComposition

      totalCashflow cashIn cashOut totalPnl unrealizedPnl realizedPnl dividendYield
      rsi14d movingAvg50d movingAvg200d

      implementations {
        holdingSecuritySymbol securityName assetClassId sebiCategoryId
        weightWithinAssetModel weightWithinSebiCategory
      }
      holdingReturnContributions { holdingSecuritySymbol weight return1y return3y }
      holdingRiskContributions {
        holdingSecuritySymbol weight volatility trackingError beta
        maxDrawdown maxDrawdown6m maxDrawdown1y maxDrawdown3y maxDrawdown5y
      }
      priceSeries { date return1d }
    }
  }
`;

/**
 * The SEBI categories' display names. `value` is the category's name exactly
 * as `getAssetClassesWithSebiCategories` and the fund master spell it
 * ("Large Cap Fund"), so it is matched on that; `displayValue` is the label.
 */
export const GET_SEBI_CATEGORY_LABELS = gql`
  query GetSebiCategoryLabels($language: String!) {
    getSebiMutualFundCategoryEnum(language: $language) {
      value
      displayValue
    }
  }
`;

export const GET_ASSET_MODEL_CATALOGUE = gql`
  query GetAssetModelCatalogue($limit: Int = 200) {
    getAssetModels(limit: $limit) {
      id
      name
      source
      category
      description
      riskPreference
      timeHorizon
      isActive
      isListable
    }
  }
`;

export const GET_ASSET_MODELS = gql`
  # onlyListed is Boolean! on the resolver — passing null rejects the whole
  # query, which is why the register read as empty while 40 models sat in it.
  query GetAssetModels($riskPreference: String, $limit: Int = 200, $onlyListed: Boolean! = false) {
    getAssetModelCompleteData(riskPreference: $riskPreference, limit: $limit, onlyListed: $onlyListed) {
      id
      name
      modelStandardName
      riskPreference
      timeHorizon
      timeHorizonYears
      isListable
      broadAllocations { assetClassName weight toleranceBand }
      subclassAllocations {
        assetClassName sebiCategoryName weightWithinAssetClass weightWithinAssetModel
        minAllocation maxAllocation
      }
    }
  }
`;

/**
 * One model's icon, read on its own.
 *
 * Separate from GET_ASSET_MODEL_DETAIL on purpose: `iconUrl` is new on the
 * backend, and a server that has not deployed it rejects any document asking
 * for it. Kept apart, that refusal costs only the icon — the model page itself
 * still loads. Fold it into the detail query once every server has the field.
 */
export const ASSET_MODEL_ICON = gql`
  query AssetModelIcon($id: Int!) {
    getAssetModelById(id: $id) {
      id
      iconUrl
    }
  }
`;
